import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wrench, Receipt, Copy, Check } from "lucide-react";
import useSeo from "../lib/useSeo";

/**
 * SII retention rate for boletas de honorarios (personas naturales).
 * Chile — schedule of retention that goes up +0.75pp per year until 17%.
 */
const RETENTION_RATE = 0.1525; // 15.25% for calendar year 2026
const RETENTION_LABEL = "15,25%";
const RATE_PERIOD_LABEL = "Tasa desde el 1 de enero al 31 de diciembre 2026 (15,25%)";

const fmtCLP = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(
    Math.round(n || 0),
  );

const parseAmount = (raw) => {
  const digits = String(raw ?? "").replace(/[^\d]/g, "");
  return digits ? parseInt(digits, 10) : 0;
};
const groupInt = (n) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

export default function BoletaCalculator() {
  useSeo({
    title: "Calculadora de Boleta de Honorarios · Utilidades DoSoft",
    description:
      "Calcula boletas de honorarios en Chile en segundos: pasa de líquido a bruto o de bruto a líquido con la retención vigente 2026 (15,25%).",
    canonical: "/utilidades/boleta",
  });

  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const prev = el?.getAttribute("content");
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,nofollow");
    return () => {
      if (prev) el.setAttribute("content", prev);
      else el.parentNode?.removeChild(el);
    };
  }, []);

  const [amountRaw, setAmountRaw] = useState("");
  const amount = parseAmount(amountRaw);

  // Both scenarios are computed at once.
  //   As LÍQUIDO: user typed the amount they want to receive.
  //     boleta_bruta = liquido / (1 - r)   ;   retencion = boleta - liquido
  //   As BRUTA:   user typed the boleta amount.
  //     liquido = bruta * (1 - r)          ;   retencion = bruta - liquido
  const { asLiquido, asBruta } = useMemo(() => {
    if (!amount) {
      return {
        asLiquido: { boleta: 0, retencion: 0, liquido: 0 },
        asBruta: { boleta: 0, retencion: 0, liquido: 0 },
      };
    }
    const boletaFromLiquido = Math.round(amount / (1 - RETENTION_RATE));
    const boletaFromBruta = amount;
    return {
      asLiquido: {
        boleta: boletaFromLiquido,
        retencion: boletaFromLiquido - amount,
        liquido: amount,
      },
      asBruta: {
        boleta: boletaFromBruta,
        retencion: Math.round(boletaFromBruta * RETENTION_RATE),
        liquido: boletaFromBruta - Math.round(boletaFromBruta * RETENTION_RATE),
      },
    };
  }, [amount]);

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copiado: ${fmtCLP(value)}`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="bg-ash-100 min-h-[70vh]">
      <main className="max-w-5xl mx-auto px-6 py-8 md:py-12">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brand-slate font-mono mb-1 flex items-center gap-2">
            <Wrench size={12} strokeWidth={1.8} /> Herramientas DoSoft
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight text-ink-900">
            Calculadora de Boleta de Honorarios
          </h1>
          <p className="text-ink-800/70 text-sm md:text-base mt-3">
            Escribe el monto una sola vez y ve al instante ambos escenarios: cuánto emitir si acordaste el líquido, o cuánto recibirás si acordaste el bruto.
          </p>
        </div>

        <div className="rounded-2xl overflow-hidden border border-ink-500 bg-white shadow-lg">
          {/* Retention banner */}
          <div className="bg-ink-900 text-brand text-center py-3 text-sm font-medium">
            {RATE_PERIOD_LABEL}
          </div>

          {/* Amount input */}
          <div className="p-6 md:p-8 border-b border-ash-100/60">
            <div className="text-[11px] uppercase tracking-widest font-mono text-ink-800/60 mb-3">
              ¿Cuánto necesitas calcular?
            </div>
            <div className="flex items-center border-2 border-brand rounded-xl px-5 py-4 focus-within:border-brand-active transition-colors">
              <span className="font-display font-black text-3xl md:text-4xl text-brand mr-3">$</span>
              <input
                data-testid="boleta-input"
                type="text"
                inputMode="numeric"
                autoFocus
                placeholder="0"
                value={amountRaw ? groupInt(parseAmount(amountRaw)) : ""}
                onChange={(e) => setAmountRaw(e.target.value)}
                className="w-full font-display font-black text-3xl md:text-4xl text-ink-900 bg-transparent outline-none placeholder:text-ash-500/40"
              />
            </div>
            <div className="text-[11px] text-ink-800/50 mt-2 font-mono">
              Ingresa el monto acordado — evaluamos automáticamente si es líquido o bruto.
            </div>
          </div>

          {/* Two scenarios */}
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-ash-100/70">
            <Scenario
              testid="boleta-scenario-liquido"
              header="LÍQUIDO"
              description={
                <>
                  Si lo pactado fue en <strong>valores líquidos</strong>, deberás emitir la boleta por{" "}
                  <CopyPill label="Bruto" value={asLiquido.boleta} onCopy={() => copy("Bruto", asLiquido.boleta)} />{" "}
                  y recibirás{" "}
                  <CopyPill label="Líquido" value={asLiquido.liquido} onCopy={() => copy("Líquido", asLiquido.liquido)} highlight />.
                </>
              }
              detail={{ label: "Retención SII", value: asLiquido.retencion }}
              amount={amount}
            />
            <Scenario
              testid="boleta-scenario-bruta"
              header="BRUTA"
              description={
                <>
                  Si lo pactado fue en <strong>valores bruto</strong>, deberás emitir la boleta por{" "}
                  <CopyPill label="Bruto" value={asBruta.boleta} onCopy={() => copy("Bruto", asBruta.boleta)} highlight />{" "}
                  y recibirás{" "}
                  <CopyPill label="Líquido" value={asBruta.liquido} onCopy={() => copy("Líquido", asBruta.liquido)} />.
                </>
              }
              detail={{ label: "Retención SII", value: asBruta.retencion }}
              amount={amount}
            />
          </div>
        </div>

        <div className="mt-8 text-xs text-ink-800/50 space-y-2">
          <div>
            <strong>Retención:</strong> a partir del 1 de enero de 2026 el SII aplica una retención del{" "}
            {RETENTION_LABEL} sobre el monto bruto de la boleta de honorarios. La escalera de tasas
            aumenta 0,75 pp cada año hasta llegar al 17% en 2028.
          </div>
          <div>
            <strong>Fórmulas:</strong> bruto = líquido ÷ (1 − 0,1525) · retención = bruto × 0,1525 ·
            líquido = bruto × 0,8475. Los montos se redondean al peso más cercano.
          </div>
        </div>
      </main>
    </div>
  );
}

function Scenario({ header, description, detail, amount, testid }) {
  return (
    <div data-testid={testid} className="p-6 md:p-8">
      <div className="text-center mb-4">
        <div className="inline-block text-[11px] uppercase tracking-[0.25em] font-mono font-bold text-ink-900 border-b-2 border-brand pb-1 px-3">
          {header}
        </div>
      </div>
      <p className="text-sm md:text-[15px] text-ink-800 leading-relaxed text-center">
        {description}
      </p>
      {amount > 0 && (
        <div className="mt-4 text-center">
          <div className="text-[10px] uppercase tracking-widest font-mono text-ink-800/50">
            {detail.label}
          </div>
          <div className="font-mono text-sm text-ink-800/70 mt-0.5">{fmtCLP(detail.value)}</div>
        </div>
      )}
    </div>
  );
}

function CopyPill({ label, value, onCopy, highlight }) {
  const [copied, setCopied] = useState(false);
  const handle = (e) => {
    e.preventDefault();
    if (value <= 0) return;
    onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handle}
      title={`Copiar ${label}`}
      className={`inline-flex items-center gap-1 font-bold underline decoration-2 underline-offset-4 transition-colors ${
        highlight ? "text-brand hover:text-brand-active" : "text-ink-900 hover:text-brand"
      }`}
    >
      {copied ? (
        <Check size={11} strokeWidth={3} className="text-[#0E6B2A]" />
      ) : (
        <Copy size={11} strokeWidth={1.8} className="opacity-0 group-hover:opacity-100" />
      )}
      {fmtCLP(value)}
    </button>
  );
}
